import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | expired
  const [details, setDetails] = useState(null);
  const attemptsRef = useRef(0);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setState("paid");
          // Save session for the mandatory registration step
          localStorage.setItem("mep_pending_session", sessionId);
          // Clear any stale auth so the user is forced through registration
          logout();
          return;
        }
        if (data.status === "expired" || attemptsRef.current >= 12) {
          setState(data.status === "expired" ? "expired" : "failed");
          return;
        }
        setTimeout(poll, 2500);
      } catch {
        if (attemptsRef.current >= 5) setState("failed");
        else setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, logout]);

  return (
    <div className="min-h-screen bg-[var(--mep-black)] text-white mep-grain flex items-center justify-center px-5 py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 size={48} className="animate-spin mx-auto text-[var(--mep-gold)]" />
            <h1 className="font-display text-3xl md:text-4xl font-light mt-8 tracking-tighter">Confirmando tu pago...</h1>
            <p className="text-[var(--mep-text-muted)] mt-3">Esto puede tardar unos segundos. No cierres la ventana.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[rgba(212,175,55,0.12)] border border-[var(--mep-gold)]/40 flex items-center justify-center mx-auto">
              <CheckCircle2 size={48} className="text-[var(--mep-gold)]" />
            </div>
            <div className="mep-pill-pink mt-6 mx-auto inline-flex">Acceso desbloqueado</div>
            <h1 className="font-display text-4xl md:text-5xl font-light mt-4 tracking-tighter">
              ¡Bienvenido a <span className="font-accent text-[var(--mep-gold)]">Mi Entrenador Personal</span>!
            </h1>
            <p className="text-[var(--mep-text-muted)] mt-4 leading-relaxed">
              Tu pago fue confirmado correctamente. Ya tienes acceso completo a todas las rutinas, comidas saludables,
              seguimiento de progreso y planes fitness.
            </p>
            {details && (
              <div className="mt-6 inline-block text-xs text-[var(--mep-text-muted)] tracking-[0.18em] uppercase">
                Pago: {details.amount} {details.currency?.toUpperCase()} · {details.country}
              </div>
            )}
            <div className="mt-10">
              <Link to="/registro" data-testid="success-register-link" className="btn-pink px-8 py-4 font-semibold inline-block">
                Crear mi cuenta
              </Link>
            </div>
          </>
        )}

        {(state === "failed" || state === "expired") && (
          <>
            <XCircle size={48} className="mx-auto text-red-400" />
            <h1 className="font-display text-3xl md:text-4xl font-light mt-8 tracking-tighter">
              {state === "expired" ? "Sesión expirada" : "No pudimos confirmar tu pago"}
            </h1>
            <p className="text-[var(--mep-text-muted)] mt-3">Por favor intenta de nuevo. No se realizó ningún cargo si la sesión expiró.</p>
            <button
              data-testid="success-retry-btn"
              onClick={() => navigate("/pago")}
              className="btn-pink px-8 py-4 font-semibold mt-8"
            >
              Reintentar pago
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
